import React from 'react';
import { ResponsiveModal } from '../../ui/ResponsiveModal';
import { FileCheck, Upload, Loader, Calendar } from 'lucide-react';

interface ContractUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpload: () => Promise<void>;
  contractFile: File | null;
  uploadingContract: boolean;
  dragActive: boolean;
  fileInputRef: React.RefObject<HTMLInputElement>;
  onFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onDragOver: (e: React.DragEvent<HTMLDivElement>) => void;
  onDragLeave: (e: React.DragEvent<HTMLDivElement>) => void;
  onDrop: (e: React.DragEvent<HTMLDivElement>) => void;
  onRemoveFile: () => void;
  signingDate: Date;
  onSigningDateChange: (date: Date) => void;
  translations: any;
}

const ContractUploadModal: React.FC<ContractUploadModalProps> = ({
  isOpen,
  onClose,
  onUpload,
  contractFile,
  uploadingContract,
  dragActive,
  fileInputRef,
  onFileChange,
  onDragOver,
  onDragLeave,
  onDrop,
  onRemoveFile,
  signingDate,
  onSigningDateChange,
  translations: t
}) => {
  // Format date for input field (YYYY-MM-DD)
  const formatDateForInput = (date: Date | undefined): string => {
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Handle date input change
  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newDate = new Date(e.target.value);
    if (!isNaN(newDate.getTime())) {
      onSigningDateChange(newDate);
    }
  };

  // Get today's date for max attribute
  const today = formatDateForInput(new Date());
  return (
    <ResponsiveModal
      isOpen={isOpen}
      onClose={onClose}
      title={t.contract?.upload || "Upload Contract"}
      size="md"
    >
      <div className="space-y-4">
        <p className="text-black/70 dark:text-white/70">{t.contract?.uploadDescription || "Upload a signed contract document"}</p>

        <div
          className={`border-2 border-dashed ${dragActive ? 'border-green-500 bg-green-50 dark:bg-green-900/30' : 'border-black/20 dark:border-gray-600'} rounded-lg p-6 text-center transition-colors duration-200 bg-white/50 dark:bg-gray-800/50`}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
          onDragEnter={onDragOver}
        >
          <input
            type="file"
            accept=".pdf"
            ref={fileInputRef}
            onChange={onFileChange}
            className="hidden"
          />
          {contractFile ? (
            <div className="space-y-2">
              <div className="flex items-center justify-center">
                <FileCheck className="h-10 w-10 text-green-600 dark:text-green-400" />
              </div>
              <p className="text-sm font-medium text-black dark:text-white">{contractFile.name}</p>
              <p className="text-xs text-black/60 dark:text-white/60">
                {(contractFile.size / 1024 / 1024).toFixed(2)} MB
              </p>
              <button
                type="button"
                onClick={onRemoveFile}
                className="text-sm text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300"
              >
                {t.contract?.remove || "Remove"}
              </button>
            </div>
          ) : (
            <div
              className="space-y-2 cursor-pointer"
              onClick={() => fileInputRef.current?.click()}
            >
              <div className="flex items-center justify-center">
                <Upload className={`h-10 w-10 ${dragActive ? 'text-green-500 dark:text-green-400' : 'text-black/40 dark:text-white/40'}`} />
              </div>
              <p className="text-sm font-medium text-black dark:text-white">{t.contract?.selectFile || "Select a file"}</p>
              <p className="text-xs text-black/60 dark:text-white/60">{t.contract?.dragDrop || "or drag and drop"}</p>
            </div>
          )}
        </div>

        {/* Date Picker Section */}
        <div className="space-y-2">
          <label className="flex items-center text-sm font-medium text-black/70 dark:text-white/70">
            <Calendar className="h-4 w-4 mr-2" />
            Data di Firma del Contratto
          </label>
          <input
            type="date"
            value={formatDateForInput(signingDate)}
            onChange={handleDateChange}
            max={today}
            className="w-full px-3 py-2 border border-black/20 dark:border-gray-600 rounded-lg bg-white/50 dark:bg-gray-800/50 text-black dark:text-white focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors"
          />
          <p className="text-xs text-black/60 dark:text-white/60">
            Seleziona la data in cui il contratto è stato effettivamente firmato
          </p>
        </div>

        <div className="flex justify-end space-x-3 pt-4">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-black dark:text-white glass-card rounded-lg hover:bg-black/5 dark:hover:bg-white/5"
          >
            {t.form?.buttons?.cancel || "Cancel"}
          </button>
          <button
            type="button"
            onClick={onUpload}
            disabled={!contractFile || uploadingContract}
            className={`px-4 py-2 text-sm font-medium text-white border border-transparent rounded-lg ${
              !contractFile || uploadingContract
                ? 'bg-green-500/50 cursor-not-allowed'
                : 'bg-green-600 hover:bg-green-700'
            }`}
          >
            {uploadingContract ? (
              <>
                <span className="mr-2">{t.contract?.uploading || "Uploading..."}</span>
                <Loader className="h-4 w-4 inline animate-spin" />
              </>
            ) : (
              <>
                <FileCheck className="h-4 w-4 mr-2 inline" />
                {t.contract?.uploadAndAccept || "Upload and Accept Deal"}
              </>
            )}
          </button>
        </div>
      </div>
    </ResponsiveModal>
  );
};

export default ContractUploadModal;